import urlConstants from '~/lib/constants/url.constants';

import api from './api';
import {
  AppointmentStatus,
  RecurringAppointmentUpdateOption,
} from '~/lib/constants/calendar.constants';
import { joinQueryParams } from '~/lib/util/api';
import { HttpRequestMethod } from '..';
import { isEmpty } from 'lodash';
import { CacheTag } from './CacheTag';
import { executeBaseQuery } from './utils';
import type { NoteResponse } from '~/lib/types/notes/NoteResponse';
import type { Recurrence } from '~/lib/util/calendar.util';

export interface AppointmentsWithNotesResponse {
  appointmentDetails: GetAppointmentDetailsResponse;
  notes: NoteResponse[];
}

type SaveAppointmentRequest = {
  appointmentEndDateTime: string;
  appointmentStartDateTime: string;
  appointmentType: string;
  hostExternalId: string;
  ignoreAvailabilitySettings: boolean;
  inquiryExternalId?: string;
  isPatientAttending: boolean;
  patientExternalId: string;
  providerAttendeeExternalIds: string[];
  recurringAppointmentUntilDateTime?: string;
  recurrenceFrequency: Recurrence;
  status: AppointmentStatus;
  supportAttendeeExternalIds: string[];
  timeZoneId: string;
  isRescheduleRequest?: boolean;
};

export type BatchAppointmentEntry = Omit<SaveAppointmentRequest, 'status'>;

export type BatchSaveAppointmentsRequest = {
  appointments: BatchAppointmentEntry[];
};

export type BatchAppointmentResult = {
  status: number;
  appointmentType?: string;
  appointmentExternalId?: string;
  message?: string;
};

export type BatchSaveAppointmentsResponse = {
  status: number;
  data: BatchAppointmentResult[];
};

type GetAppointmentDetailsRequest = {
  endDateISO?: string;
  pageNumber?: number;
  pageSize: number;
  patientId?: string;
  inquiryIds?: string[];
  startDateISO?: string;
  statuses?: AppointmentStatus[];
  includeTimezoneDetails?: boolean;
};

type GetAppointmentDateTimeDetailsRequest = {
  endDateISO?: string;
  patientId?: string;
  startDateISO?: string;
};

export interface LastJoinedAppointmentResponse {
  appointmentExternalId: string;
  appointmentStartDateTime: string;
  appointmentType: string;
  appointmentTypeLabel: string;
  host: AppointmentAttendee | null;
  subject?: string | null;
}

type AppointmentDetailsFilter = {
  ['start-date']?: string;
  ['end-date']?: string;
  ['patient-external-id']?: string;
  ['inquiry-external-id']?: string[];
  status?: AppointmentStatus[];
};
export interface CancellationSubReason {
  code: string;
  subReason: string;
}
export interface CancellationReason {
  code: string;
  reason: string;
  subReasons?: CancellationSubReason[];
}
// TODO explore using our getAppointmentDetails query for this instead of a duplicate definition
const fetchAppointmentDetails = async ({
  baseQuery,
  startDate,
  filterByKey,
  filterByValue,
  endDate,
  statuses,
  pageSize,
  includeTimezoneDetails,
  shouldIncludeCancelled,
}) => {
  const params = {
    'filter-by': joinQueryParams({
      ...(endDate && { 'end-date': endDate }),
      [filterByKey]: filterByValue,
      'start-date': startDate,
      status: statuses ?? [
        AppointmentStatus.COMPLETED,
        AppointmentStatus.SCHEDULED,
        ...(shouldIncludeCancelled ? [AppointmentStatus.CANCELLED] : []),
      ],
    }),
    'include-cancelled': shouldIncludeCancelled,
    'page-size': pageSize ?? 200,
  };

  if (includeTimezoneDetails) {
    params['include-timezone-details'] = includeTimezoneDetails;
  }
  const url = urlConstants.calendar.getAppointmentDetails;
  const appointmentDetailsResponse = await executeBaseQuery({
    baseQuery,
    params,
    url,
  });

  return appointmentDetailsResponse;
};

const fetchPatientAppointmentNotes = async ({
  appointmentDetails,
  baseQuery,
}) => {
  const appointmentsWithPatients = (appointmentDetails?.data ?? [])
    .filter((appointment) => appointment.patient)
    .map((appointment: AppointmentDetailResponse) => ({
      appointmentExternalId: appointment.appointmentExternalId,
      patientExternalId: appointment.patient.externalId,
    }));

  if (!isEmpty(appointmentsWithPatients)) {
    const patientExternalIds = [
      ...new Set(
        appointmentsWithPatients.map((appt) => appt.patientExternalId),
      ),
    ];

    const params = {
      'page-number': 1,
      'page-size': 100,
      patientExternalIds,
    };
    const url = urlConstants.clinical_data.getNotesForPatient;
    const patientNotes = await executeBaseQuery({ baseQuery, params, url });
    return patientNotes;
  }
  return [];
};
type CancelAppointmentRequest = {
  appointmentExternalId: string;
  cancelType: RecurringAppointmentUpdateOption;
  cancellationReason: string;
  cancellationSubReason?: string;
  rescheduleAppointmentExternalId?: string;
  inquiryExternalId?: string;
};

export const appointmentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    cancelAppointment: build.mutation<void, CancelAppointmentRequest>({
      invalidatesTags: (_result, _error, arg) => [
        CacheTag.PatientAppointmentsWithNotes,
        CacheTag.AppointmentDetails,
        ...(arg.inquiryExternalId
          ? [{ id: arg.inquiryExternalId, type: CacheTag.Inquiry }]
          : []),
      ],

      query: ({
        appointmentExternalId,
        cancelType,
        cancellationReason,
        cancellationSubReason,
        rescheduleAppointmentExternalId,
      }) => ({
        body: {
          all: cancelType === RecurringAppointmentUpdateOption.ALL,
          appointmentExternalId,
          cancellationReason,
          ...(rescheduleAppointmentExternalId && {
            rescheduleAppointmentExternalId,
          }),
          status: AppointmentStatus.CANCELLED,
          ...(cancellationSubReason && { cancellationSubReason }),
        },
        method: HttpRequestMethod.PATCH,
        url: urlConstants.calendar.cancelAppointment,
      }),
    }),
    getCancellationReasons: build.query<{ data: CancellationReason[] }, void>({
      query: () => {
        return {
          method: HttpRequestMethod.GET,
          url: urlConstants.calendar.cancellationReasons,
        };
      },
    }),

    getAppointmentDetails: build.query<
      GetAppointmentDetailsResponse,
      GetAppointmentDetailsRequest
    >({
      keepUnusedDataFor: 5 * 60, // Cache for 5 minutes to avoid re-fetching between modal steps
      providesTags: (_, __, request) => [
        CacheTag.AppointmentDetails,
        ...(request.inquiryIds?.map((id) => ({ id, type: CacheTag.Inquiry })) ??
          []),
      ],
      query: ({
        startDateISO,
        endDateISO,
        patientId,
        includeTimezoneDetails,
        inquiryIds,
        statuses,
        pageNumber,
        pageSize,
      }) => {
        const filterBy: AppointmentDetailsFilter = {};
        if (startDateISO) {
          filterBy['start-date'] = startDateISO;
        }
        if (endDateISO) {
          filterBy['end-date'] = endDateISO;
        }
        if (patientId) {
          filterBy['patient-external-id'] = patientId;
        }
        if (inquiryIds) {
          filterBy['inquiry-external-id'] = inquiryIds;
        }
        if (statuses) {
          filterBy.status = statuses;
        }
        const params: any = {
          'filter-by': joinQueryParams(filterBy),
          'page-number': pageNumber || 1,
          'page-size': pageSize,
        };

        if (includeTimezoneDetails) {
          params['include-timezone-details'] = includeTimezoneDetails;
        }
        return {
          url: urlConstants.calendar.getAppointmentDetails,
          params,
        };
      },
    }),

    getAppointmentDateTimeDetails: build.query<
      AppointmentScheduleDatetimeResponse[],
      GetAppointmentDateTimeDetailsRequest
    >({
      query: ({ startDateISO, endDateISO, patientId }) => {
        return {
          params: {
            'appointment-end-date': endDateISO,
            'appointment-start-date': startDateISO,
            'patient-id': patientId,
          },
          url: urlConstants.calendar.getAppointmentDateTimeDetails,
        };
      },
    }),

    getLastJoinedAppointment: build.query<
      LastJoinedAppointmentResponse | null,
      string
    >({
      query: (patientId) => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.calendar.getLastJoinedAppointment(patientId),
      }),
    }),

    getAppointmentsWithNotes: build.query<
      AppointmentsWithNotesResponse,
      {
        endDate?: string;
        filterByKey: string;
        filterByValue: string;
        pageSize?: number;
        startDate: string;
        statuses?: AppointmentStatus[];
        includeTimezoneDetails?: boolean;
        shouldIncludeCancelled?: boolean;
      }
    >({
      providesTags: [
        CacheTag.PatientAppointmentsWithNotes,
        CacheTag.GetPatientById,
      ],
      queryFn: async (
        {
          startDate,
          filterByKey,
          filterByValue,
          endDate,
          statuses,
          pageSize,
          includeTimezoneDetails,
          shouldIncludeCancelled,
        },
        _,
        __,
        baseQuery,
      ) => {
        const appointmentDetailsResponse = await fetchAppointmentDetails({
          baseQuery,
          endDate,
          filterByKey,
          filterByValue,
          includeTimezoneDetails,
          shouldIncludeCancelled,
          pageSize,
          startDate,
          statuses,
        });

        if (appointmentDetailsResponse.error)
          return { error: appointmentDetailsResponse.error };

        const appointmentDetails =
          appointmentDetailsResponse?.data as GetAppointmentDetailsResponse;

        const notesResponse = await fetchPatientAppointmentNotes({
          appointmentDetails,
          baseQuery,
        });

        if (notesResponse?.error) return { error: notesResponse?.error };

        const notes = notesResponse?.data ?? [];

        return {
          data: {
            appointmentDetails,
            notes,
          },
        };
      },
    }),
    saveAppointment: build.mutation<string, SaveAppointmentRequest>({
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;

          if (arg.isRescheduleRequest) return;

          dispatch(
            api.util.invalidateTags([
              CacheTag.PatientAppointmentsWithNotes,
              CacheTag.AppointmentDetails,
            ]),
          );

          if (arg.inquiryExternalId) {
            dispatch(
              api.util.invalidateTags([
                { id: arg.inquiryExternalId, type: CacheTag.Inquiry },
              ]),
            );
          }
        } catch (err) {
          console.error('Save appointment failed:', err);
        }
      },
      query: (req: SaveAppointmentRequest) => ({
        body: req,
        method: HttpRequestMethod.POST,
        responseHandler: 'text',
        url: urlConstants.calendar.saveAppointment,
      }),
    }),

    batchSaveAppointments: build.mutation<
      BatchSaveAppointmentsResponse,
      BatchSaveAppointmentsRequest
    >({
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(
            api.util.invalidateTags([
              CacheTag.PatientAppointmentsWithNotes,
              CacheTag.AppointmentDetails,
            ]),
          );
          const inquiryExternalIds = [
            ...new Set(
              arg.appointments.map((a) => a.inquiryExternalId).filter(Boolean),
            ),
          ];
          if (inquiryExternalIds.length > 0) {
            dispatch(
              api.util.invalidateTags(
                inquiryExternalIds.map((id) => ({
                  id,
                  type: CacheTag.Inquiry,
                })),
              ),
            );
          }
        } catch {
          // Error propagates via RTK Query's error state
        }
      },
      query: (req: BatchSaveAppointmentsRequest) => ({
        body: req,
        method: HttpRequestMethod.POST,
        url: urlConstants.calendar.batchSaveAppointments,
      }),
    }),

    getCronofyEventUids: build.query<
      string[],
      {
        appointmentExternalId: string;
        includeAllRecurrences?: boolean;
      }
    >({
      query: ({ appointmentExternalId, includeAllRecurrences }) => ({
        method: HttpRequestMethod.GET,
        params: { includeAllRecurrences },
        url: urlConstants.calendar.getCronofyEventUids(appointmentExternalId),
      }),
    }),

    getRecurringAppointmentDateTimesByAppointmentExternalId: build.query<
      AppointmentScheduleDatetimeResponse[],
      string
    >({
      query: (appointmentExternalId: string) => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.calendar.recurringAppointments(appointmentExternalId),
      }),
    }),
  }),
});

export const {
  useGetAppointmentDetailsQuery,
  useGetLastJoinedAppointmentQuery,
  useGetAppointmentsWithNotesQuery,
  useCancelAppointmentMutation,
  useSaveAppointmentMutation,
  useBatchSaveAppointmentsMutation,
  useGetCancellationReasonsQuery,
  useLazyGetAppointmentDateTimeDetailsQuery,
  useGetAppointmentDateTimeDetailsQuery,
  useGetCronofyEventUidsQuery,
  useGetRecurringAppointmentDateTimesByAppointmentExternalIdQuery,
} = appointmentsApi;
